import React from 'react';
import { useTranslation } from 'react-i18next';

interface PricingSectionProps {
    onGetStarted: (planName: string) => void;
}

interface Plan {
    /** Plan name: a product name, shown as is and passed back through onGetStarted. */
    name: string;
    /** Key under `pricing.plans` in the landing namespace. */
    id: 'free' | 'coreHr' | 'complete' | 'enterprise';
    price: string;
    cta: 'startFree' | 'getStarted' | 'contactUs';
    highlighted?: boolean;
}

/**
 * Rates follow the published ladder in docs/decisions/2026-08-14-saas-pricing-model.md,
 * restricted to the modules this product ships: Core HR ৳20 + Payroll ৳10 = ৳30 for the
 * full suite. Enterprise's ৳9 floor is that ৳30 at the published 1,001+ band (30%).
 *
 * Keep plan names in sync with PLAN_OPTIONS in CreateInstitution.tsx — the name is passed
 * back through onGetStarted and preselects the plan in the signup modal.
 * The captions, taglines and features live in the landing namespace (pricing.plans.<id>).
 */
const plans: Plan[] = [
    { name: 'Free', id: 'free', price: '৳0', cta: 'startFree' },
    { name: 'Core HR', id: 'coreHr', price: '৳20', cta: 'getStarted' },
    { name: 'Complete', id: 'complete', price: '৳30', cta: 'getStarted', highlighted: true },
    { name: 'Enterprise', id: 'enterprise', price: 'custom', cta: 'contactUs' },
];

const PricingSection: React.FC<PricingSectionProps> = ({ onGetStarted }) => {
    const { t } = useTranslation('landing');
    return (
    <section className="lp-pricing" id="pricing">
        <div className="lp-shell">
            <p className="lp-eyebrow lp-eyebrow-dark">{t('pricing.eyebrow')}</p>
            <h2 className="lp-section-title">{t('pricing.title')}</h2>
            <p className="lp-section-sub">{t('pricing.sub')}</p>

            <ul className="lp-plan-grid">
                {plans.map((plan) => {
                    const features = t(`pricing.plans.${plan.id}.features`, { returnObjects: true }) as unknown as string[];
                    const isAmount = /^৳/.test(plan.price);
                    return (
                    <li
                        className={`lp-plan${plan.highlighted ? ' lp-plan-featured' : ''}`}
                        key={plan.name}
                    >
                        {plan.highlighted && <span className="lp-plan-flag">{t('pricing.bestValue')}</span>}
                        <h3 className="lp-plan-name">{plan.name}</h3>
                        <p className="lp-plan-price">
                            <span
                                className={`lp-plan-amount${isAmount ? '' : ' lp-plan-amount-word'}`}
                            >
                                {isAmount ? plan.price : t('pricing.custom')}
                            </span>
                            <span className="lp-plan-caption">{t(`pricing.plans.${plan.id}.caption`)}</span>
                        </p>
                        <p className="lp-plan-tagline">{t(`pricing.plans.${plan.id}.tagline`)}</p>
                        <ul className="lp-plan-features">
                            {features.map((feature) => (
                                <li className="lp-plan-feature" key={feature}>{feature}</li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            className={`lp-btn lp-btn-block ${plan.highlighted ? 'lp-btn-primary' : 'lp-btn-outline'}`}
                            onClick={() => onGetStarted(plan.name)}
                        >
                            {t(`pricing.cta.${plan.cta}`)}
                        </button>
                    </li>
                    );
                })}
            </ul>

            <p className="lp-finePrint">{t('pricing.finePrint')}</p>
        </div>
    </section>
    );
};

export default PricingSection;
